import { VISIT_ASSIGNMENT_DATE_RECOMMENDATIONS_QUERY } from '@/components/decisionSupport/queries/visitAssignment';
import {
    VisitAssignmentDateRecommendationsQuery,
    VisitAssignmentDateRecommendationsQueryVariables,
} from '@/components/decisionSupport/queries/visitAssignment.gql.types';
import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';

import { VisitOverrideState, buildVisitAssignmentInput } from '../utils/visitAssignmentInput.utils';

type DateOutput = NonNullable<
    VisitAssignmentDateRecommendationsQuery['decisionSupport']['visitAssignment']['dateRecommendations']
>;
type DateResult = DateOutput['results'][number];
export type AssignmentDateRecommendation = DateResult['dateRecommendation'];

export type UseAssignmentDateRecsResult = {
    // Endpoint-level factor/constraint definitions — identical across all visits.
    definitions: DateOutput['definitions'] | null;
    // Per-visit recommendations keyed by visit ID for O(1) lookup.
    resultsByVisitId: ReadonlyMap<string, DateResult>;
    loading: boolean;
    error: ErrorLike | undefined;
};

const EMPTY_RESULTS = new Map<string, DateResult>();

// Fires the date-recommendations query (clinicians held fixed) for the visit queue.
// Each visit must carry its held clinician(s) via `appointments` so the engine
// scores dates against them; with no clinician held the engine returns empty
// recommendations for that visit.
export const useAssignmentDateRecs = (
    visits: VisitOverrideState[],
): UseAssignmentDateRecsResult => {
    const input = useMemo(() => buildVisitAssignmentInput(visits), [visits]);

    const { data, loading, error } = useQuery<
        VisitAssignmentDateRecommendationsQuery,
        VisitAssignmentDateRecommendationsQueryVariables
    >(VISIT_ASSIGNMENT_DATE_RECOMMENDATIONS_QUERY, {
        variables: { input },
        skip: input.visits.length === 0,
        fetchPolicy: 'network-only',
    });

    const output = data?.decisionSupport.visitAssignment.dateRecommendations ?? null;

    const resultsByVisitId = useMemo(() => {
        if (!output) {
            return EMPTY_RESULTS;
        }
        const map = new Map<string, DateResult>();
        output.results.forEach((r) => map.set(r.visitId, r));
        return map;
    }, [output]);

    return {
        definitions: output?.definitions ?? null,
        resultsByVisitId,
        loading,
        error,
    };
};
